import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { TrendingUp, RefreshCw, Download, Trophy, DollarSign, Building2, Mail, Loader2 } from "lucide-react";

const RegionalReport = () => {
  const [period, setPeriod] = useState("month");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchReport = async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/regional/report?period=${p}`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const downloadReport = () => {
    if (!report) return;
    const text = `Regional Performance Report
Region: ${report.region.name}
Period: ${report.period_label}
Generated: ${new Date(report.generated_at).toLocaleString()}

REGION SUMMARY
- Branches: ${report.summary.branch_count}
- Total Team: ${report.summary.team_size}
- New Properties: ${report.summary.new_properties}
- New Rentals: ${report.summary.new_rentals}
- New Inquiries: ${report.summary.new_inquiries}
- Deals Created: ${report.summary.deals_created}
- Deals Won: ${report.summary.deals_won}
- Revenue: K ${report.summary.revenue.toLocaleString()}
- Win Rate: ${report.summary.win_rate}%

BRANCH PERFORMANCE
${(report.branches || [])
  .map((b, i) => `${i + 1}. ${b.name} — ${b.deals_won} won (${b.deals_total} total), K ${b.revenue.toLocaleString()}`)
  .join("\n") || "  (no branches)"}
`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `regional-report-${report.period}-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendReport = async () => {
    if (!window.confirm("Send this report by email to the General Manager?")) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/regional/report/send?period=${period}`);
      toast.success(res.data?.message || "Report sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send report");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!report) return null;

  const s = report.summary;

  return (
    <div className="space-y-6" data-testid="regional-report">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold">Regional Performance Report</h1>
          <p className="text-muted-foreground text-sm">
            {report.region.name} · {report.period_label}
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
            data-testid="period-select"
          >
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="quarter">Last 90 days</option>
            <option value="year">Last 365 days</option>
          </select>
          <Button variant="outline" onClick={() => fetchReport(period)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={downloadReport} data-testid="download-btn">
            <Download className="w-4 h-4 mr-2" />
            Download for GM
          </Button>
          <Button onClick={sendReport} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="email-report-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Email to GM
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Branches" value={s.branch_count} icon={<Building2 className="w-5 h-5" />} color="bg-blue-500" />
        <Kpi label="Team" value={s.team_size} icon={<Trophy className="w-5 h-5" />} color="bg-emerald-500" />
        <Kpi label="New Properties" value={s.new_properties} icon={<Building2 className="w-5 h-5" />} color="bg-purple-500" />
        <Kpi label="Win Rate" value={`${s.win_rate}%`} icon={<TrendingUp className="w-5 h-5" />} color="bg-amber-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Big label="Region Revenue" value={`K ${s.revenue.toLocaleString()}`} icon={<DollarSign className="w-6 h-6" />} color="text-emerald-600" />
        <Big label="Deals Won" value={s.deals_won} sub={`of ${s.deals_created}`} icon={<Trophy className="w-6 h-6" />} color="text-blue-600" />
        <Big label="New Activity" value={`${s.new_inquiries} inquiries · ${s.new_rentals} rentals`} icon={<TrendingUp className="w-6 h-6" />} color="text-purple-600" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Branch Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {report.branches.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground">No branches in this region yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Branch</th>
                  <th className="text-center p-2">Deals</th>
                  <th className="text-center p-2">Won</th>
                  <th className="text-right p-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {report.branches.map((b, i) => (
                  <tr key={b.branch_id} className="border-b" data-testid={`branch-perf-${i}`}>
                    <td className="p-2 font-medium text-gray-500">{i + 1}</td>
                    <td className="p-2 font-medium">{b.name}</td>
                    <td className="p-2 text-center">{b.deals_total}</td>
                    <td className="p-2 text-center text-green-600 font-medium">{b.deals_won}</td>
                    <td className="p-2 text-right text-emerald-600 font-semibold">
                      K {b.revenue.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Generated on {new Date(report.generated_at).toLocaleString()} · Click "Download for GM" to save and email this report.
      </p>
    </div>
  );
};

const Kpi = ({ label, value, icon, color }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`${color} text-white p-2 rounded-lg`}>{icon}</div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const Big = ({ label, value, sub, icon, color }) => (
  <Card>
    <CardContent className="pt-6 text-center">
      <div className={`${color} flex justify-center mb-2`}>{icon}</div>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

export default RegionalReport;
